/**
 * Kamus konsol portal — bahasa Indonesia.
 *
 * Tiering (`design/I18N.md`): istilah pajak/standar nasional (PB1, PPN, QRIS) tetap sama di kedua
 * bahasa; istilah antarmuka diterjemahkan.
 *
 * ⚠️ **Mode jual TIDAK diterjemahkan sebagai istilah bebas.** `table_service`/`quick_service` adalah
 * nilai enum di DB; yang diterjemahkan hanya LABELNYA, dan nilainya tak pernah ikut berubah.
 */
export const id = {
  app: { title: 'OSB POS · Portal' },
  login: {
    doorHq: 'Kantor pusat',
    doorOutlet: 'Di outlet (PIN)',
    tenant: 'Tenant',
    email: 'Email',
    password: 'Kata sandi',
    outletCode: 'Kode outlet',
    staffCode: 'Kode staf',
    pin: 'PIN',
    pinNote: 'Sesi PIN hanya untuk <b>melihat</b> — mengubah harga & master lewat pintu kantor pusat.',
    submit: 'Masuk',
    checking: 'Memeriksa…',
  },
  brands: {
    kdsWarn: 'Tiket kuning sesudah (menit)',
    /* ⚠️ Disebut sebagai KESEPAKATAN, bukan alarm: ini janji dapur kepada tamu. */
    kdsWarnHint: 'Berlaku untuk semua outlet brand ini; outlet boleh menimpanya',
    kdsLate: 'Tiket merah sesudah (menit)',
    title: 'Brand',
    new: 'Brand baru',
    empty: 'Belum ada brand.',
    businessType: 'Jenis usaha',
    businessTypeHint: 'Menentukan bentuk yang diasumsikan modul lain — F&B punya meja & dapur, retail tidak.',
    logo: 'Logo',
    logoHint: 'Tampil di layar login & chrome kasir. Kosong = nama brand dalam huruf besar.',
    logoRule: 'Harus URL lengkap (https://…).',
    frozen: 'Kode & jenis usaha tak bisa diubah: keduanya sudah dipakai struk, importir, dan modul lain.',
    subtitle: 'Satu tenant boleh punya beberapa brand. Tiap outlet wajib milik salah satunya.',
    note:
      'Brand adalah akarnya: departemen, kategori, promo, dan grup harga semuanya miliknya. ' +
      'Buat brand DULU — layar master lain dimulai dari pemilih brand.',
    deactivate: 'Mematikan brand menutup SELURUH outlet, menu, dan promonya sekaligus.',
    footer: 'Menghapus brand ditolak selama masih ada outlet, departemen, atau promo yang menunjuknya.',
  },
  /** ⭐⭐⭐ Kebijakan brand→outlet (0046). Brand memutuskan, outlet boleh menimpa. */
  policy: {
    tab: 'Kebijakan',
    brandHint:
      'Berlaku untuk SELURUH outlet brand ini. Tiap outlet boleh menimpanya sendiri di layar ' +
      'Outlet — yang dikosongkan di sana berarti ikut angka di sini.',
    outletHint:
      'Kosongkan untuk mengikuti brand. Yang diisi di sini berlaku HANYA untuk outlet ini, dan ' +
      'tak lagi berubah saat kebijakan brand diubah.',
    inherit: 'Ikut brand',
    override: 'Setel sendiri',
    groupCash: 'Kas & laci',
    groupBill: 'Bill & struk',
    groupKitchen: 'Dapur',
    groupGuest: 'Panggilan tamu',
    cashVariance: 'Toleransi selisih kas (Rp)',
    cashVarianceHint:
      'Selisih sampai sebesar ini boleh ditutup tanpa alasan. 0 = tiap rupiah selisih wajib ' +
      'dijelaskan. Selisihnya tetap dihitung dan tetap masuk berita acara — yang dilonggarkan ' +
      'hanya keharusan beralasan.',
    drawerReview: 'Buka laci ke-… ditandai',
    drawerReviewHint:
      'Buka laci sebanyak ini dalam satu sesi mulai ditandai wajib ditinjau penyelia. Yang ' +
      'diperiksa polanya, bukan satu kejadiannya.',
    reprintWindow: 'Jendela cetak ulang (menit)',
    reprintWindowHint:
      'Cetak ulang di luar jendela ini tetap BOLEH, tapi ditandai untuk ditinjau. Struk sobek ' +
      'lalu dicetak lagi semenit kemudian itu wajar; tiga jam kemudian, tidak.',
    removeSent: 'Boleh buang item yang sudah di dapur',
    removeSentHint:
      'Mati = ditolak, koreksinya lewat pembatalan beralasan. Nyala = boleh, TAPI menuntut PIN ' +
      'penyelia dan alasan, dan selalu ditandai untuk ditinjau.',
    removeSentWarning:
      'Menyalakannya bukan membuka "hapus bebas" — makanannya sudah dimasak, jadi yang dibuang ' +
      'adalah bahan yang sudah terpakai. Jejaknya menyimpan bahwa item itu sempat sampai dapur, ' +
      'supaya "berapa banyak yang kita masak lalu buang" bisa dijawab.',
    removeSentWindow: 'Batas waktu (menit)',
    removeSentWindowHint:
      'Sesudah lewat batas ini sejak item masuk dapur, membuangnya tak lagi dibuka — koreksinya ' +
      'kembali lewat pembatalan/refund. Isi 0 untuk tanpa batas waktu.',
    removeSentOff: 'mati — item yang sudah di dapur tak bisa dibuang',
    windowMeans: 'ditolak kalau sudah lewat {{n}} menit di dapur',
    forever: 'tanpa batas',
    minutes: 'menit',
    printZeroMod: 'Cetak modifier tanpa harga',
    printZeroModHint:
      'Berlaku di struk TAMU saja — tiket dapur selalu menampilkannya. Modifier BERBAYAR selalu ' +
      'tercetak apa pun setelannya.',
    callCooldown: 'Jeda antar panggilan (detik)',
    callCooldownHint: 'Satu meja tak bisa memanggil lagi sebelum jeda ini lewat.',
    callSpam: 'Ditangguhkan sesudah … panggilan iseng',
    callSpamHint: 'Dihitung dalam jendela penangguhan, bukan sepanjang masa.',
    callSuspend: 'Lama penangguhan (menit)',
    callSuspendHint: 'Selama ini, QR meja itu tak bisa memanggil sama sekali.',
    callNeedsOpen: 'Hanya saat outlet buka',
    callNeedsOpenHint:
      'QR statis bisa difoto; tanpa ini, bekas tamu bisa memanggil pelayan dari rumah tengah malam.',
  },
  roles: {
    title: 'Peran & Izin',
    subtitle: 'Izin menempel pada PERAN, dan peran dijodohkan ke JABATAN — bukan ke orang.',
    new: 'Peran baru',
    empty: 'Belum ada peran. Buat satu dulu — tanpa peran, staf tak punya izin apa pun.',
    note: 'Urutannya: buat peran → centang izinnya → petakan ke jabatan → baru staf bisa dipakai. Staf menunjuk jabatan, jabatan menunjuk peran.',
    permCount: 'Izin',
    noPerm: 'tanpa izin',
    nPerm: '{{n}} izin',
    positions: 'Jabatan',
    noPosition: 'belum dipetakan',
    positionHint: 'Ketik kode jabatan lalu Enter. Peran yang tak dipetakan ke jabatan mana pun izinnya tak pernah sampai ke siapa pun.',
    pickRole: 'Pilih peran',
    permOf: 'Izin peran',
    permBelow: 'Izin & jabatan disunting di kartu bawah — keduanya berlaku seketika, tak menunggu tombol Simpan.',
  },
  floor: {
    title: 'Lantai Outlet',
    tier: {
      column: 'Mode hitung',
      server: 'Lewat server',
      tablet: 'Tablet mandiri',
    },
    printer: {
      column: 'Printer',
      set: 'Setel',
      edit: 'Ubah',
      title: 'Profil printer',
      transport: 'Sambungan',
      net: 'Jaringan (LAN)',
      host: 'IP printer',
      port: 'Port',
      paper: 'Lebar kertas',
      codepage: 'Codepage',
      codepageHint: 'PC437 (0) aman untuk huruf Indonesia. Profil yang salah menghasilkan struk yang terbaca benar di layar tapi kacau di kertas.',
      model: 'Model',
      cutter: 'Punya pemotong',
      drawer: 'Bisa tendang laci',
      macHint: 'Ambil dari lembar self-test printer. Titik dua diisi otomatis.',
      macBad: 'MAC harus 12 digit heksadesimal, mis. 7C:6C:F0:E2:24:45',
      btHint: 'Printer Bluetooth wajib sudah di-pair lewat Setelan Android tablet ini, dan agen cetak harus terpasang. Tanpa itu ia gagal seperti "printer rusak".',
      netHint: 'Wajib IP statis atau reservasi DHCP. IP yang berpindah membuat cetak berhenti tanpa ada yang mengubah setelan apa pun.',
    },
    lastSync: 'SYNC terakhir',
    neverSynced: 'belum pernah',
    behind: 'tertinggal',
    pickOutlet: 'Pilih outlet dulu — area, meja, dan perangkat semuanya milik satu outlet.',
    delete: 'Hapus',
    roleHint: '⭐ `cashier` = till: boleh menerima pembayaran & memegang sesi kas — satu outlet boleh punya beberapa. `pos_terminal` = stasiun entri pesanan: buka bill & kirim ke dapur, TAPI tak bisa dipakai bayar walau hardware-nya sama.',
    mayTender: 'boleh terima bayaran',
    deleteTable: 'Hapus meja ini?',
    deleteDevice: 'Hapus perangkat ini?',
    deleteDeviceHint: 'Untuk perangkat yang salah ketik dan belum pernah dipakai. Yang rusak/hilang cukup DINONAKTIFKAN — jejak lama tetap bisa dijelaskan.',
    pair: 'Pasangkan',
    pairTitle: 'Kode pemasangan perangkat',
    /* ⚠️ Menyebut umur & sekali-pakainya: kode yang dikira permanen akan disimpan di grup WA. */
    pairHint: 'Ketik kode ini di tablet (layar Pasangkan Perangkat). Berlaku 15 menit, sekali pakai, dan tak bisa dilihat lagi setelah dialog ini ditutup.',
    deleteArea: 'Hapus area ini?',
    areaInUse: 'dipakai {{n}} meja',
    areas: 'Area',
    areaName: 'Nama area (mis. Lantai 1)',
    areaHint: 'Area dulu: meja tak bisa ada tanpa induknya. Ketik nama lalu Enter.',
    noArea: 'belum ada area',
    areaFirst: 'Buat areanya dulu di kartu atas.',
    tables: 'Meja',
    noTable: 'belum ada meja',
    seats: 'Kursi',
    kind: 'Jenis',
    tablesRequired: 'Outlet ini bermode table_service, jadi gerbang kesiapan menuntut minimal SATU meja sebelum sesi kas boleh dibuka.',
    devices: 'Perangkat',
    noDevice: 'belum ada perangkat',
    role: 'Peran',
    deviceHint: 'Perangkat dialamati KODE, bukan alamat IP — IP berubah, jejak transaksi tidak. Terminal kasir wajib terdaftar sebelum sesi kas bisa dibuka.',
  },
  outlets: {
    taxExempt: 'Outlet TANPA pajak',
    taxExemptHint: 'Nyalakan HANYA bila outlet ini memang tak memungut pajak apa pun. Gerbang kesiapan menuntut penugasan pajak ATAU pernyataan ini — “kosong diam-diam” tak diterima, karena itulah cara struk keluar tanpa PB1 tanpa ada yang memutuskannya.',
    readiness: 'Kesiapan',
    readinessReady: 'Siap jualan',
    /* ⚠️ Menyebut ANGKA halangannya: "belum siap" tak bisa dinilai, "3 halangan" bisa. */
    readinessBlocked: '{{n}} halangan',
    readinessOk: 'Seluruh gerbang kesiapan lolos — outlet ini boleh membuka sesi kas.',
    readinessFix: 'Buka {{layar}} →',
    readinessPassed: 'Sudah lolos ({{n}})',
    /** ⭐⭐ Kebijakan buka-kembali bill tunai — kontrol uang, bawaannya MELARANG. */
    allowReopenCash: 'Boleh buka kembali bill tunai',
    allowReopenCashHint:
      'Kasir boleh membatalkan pembayaran bill yang sudah lunas TUNAI, selama sesi kasnya masih ' +
      'terbuka dan transaksinya belum dibukukan. Mati = jawabannya refund.',
    allowReopenCashWarning:
      'Tunai satu-satunya tender yang bisa langsung masuk kantong. Menyalakan ini berarti bill ' +
      'tunai bisa dibuka lalu ditutup ulang dengan metode bayar lain atau dengan diskon — dan ' +
      'itu tak meninggalkan jejak di luar sistem. Setiap kejadiannya dicatat dan ditandai untuk ditinjau.',
    tabKitchen: 'Dapur',
    kdsOverride: 'Pakai ambang sendiri',
    kdsOverrideHint: 'Matikan untuk mengikuti setelan brand',
    kdsInherit: 'Mengikuti ambang brand — mengubahnya di layar Brand berlaku untuk outlet ini juga',
    capabilities: 'Kemampuan outlet',
    capabilityNotYet: 'belum aktif',
    capabilityHint:
      'INI yang dibaca mesin — preset hanya menyetel nilai awalnya. Mematikan `kitchen_routing` ' +
      'menghapus keharusan mengirim ke dapur sebelum menagih; menyalakannya menuntut outlet punya ' +
      'minimal satu stasiun.',
    preset: 'Jenis outlet',
    presetHint: 'Menyalakan kemampuan yang tepat (tiket dapur, barcode, potong stok) sekaligus mengisikan mode jual. Disaring menurut jenis usaha brandnya.',
    presetPickBrand: 'Pilih brand dulu',
    tabIdentity: 'Identitas',
    tabOps: 'Operasi',
    tabFiscal: 'Harga & pajak',
    tabAdmin: 'Administrasi',
    taxAfterSave: 'Pajak yang berlaku disetel sesudah outletnya tersimpan — penugasan pajak butuh outlet yang sudah ada.',
    title: 'Outlet',
    new: 'Outlet baru',
    code: 'Kode',
    name: 'Nama',
    saleMode: 'Mode jual',
    saleModeHint: 'Menentukan bentuk layar kasir. Tak ada bawaan — harus dijawab sadar.',
    timezone: 'Zona waktu',
    address: 'Alamat',
    cutoverHint:
      'Jam tutup hari adalah akar tanggal buku: resto tutup 02:00 ⇒ transaksi 01:30 masuk hari ' +
      'SEBELUMNYA. Salah setel di sini menggeser seluruh laporan harian.',
    subtitle: 'Klik satu baris untuk menyunting. Mode jual bisa diganti langsung di kolomnya.',
    needBrand: 'Buat brand-nya dulu — outlet selalu milik satu brand.',
    brandFixed: 'Tak bisa diubah sesudah dibuat: pindah brand = seluruh menu, harga, dan promo berganti.',
    frozen: 'Kode & brand tak bisa diubah. Outlet yang pindah brand adalah outlet baru, bukan penyuntingan.',
    cutover: 'Tutup hari',
    sentAt: 'Data terkirim',
    neverSent: 'belum pernah',
    offlineLimit: {
      title: 'Batas jualan luring',
      days: 'hari',
      /* ⚠️ Menyebut batasnya batas RISIKO — supaya tak ada yang mengira ini mengunci perangkat. */
      hint: 'Setelah sekian hari tak terhubung, till menahan bill BARU; bill yang sedang terbuka tetap bisa diselesaikan. Berlaku untuk seluruh outlet tenant ini.',
    },
    sendData: 'Send Data',
    sendPickBrand: 'Pilih brand',
    sendButton: 'Send Data',
    sentTo: 'Terkirim ke {{n}} outlet — tombol SYNC sekarang menyala di kasir',
    sendHint:
      'Ini belum mengubah apa pun di kasir. Tombol SYNC akan menyala di layar mereka, dan kasir ' +
      'yang menekannya — supaya harga tidak berganti sendiri di tengah antrean. Sesi kas yang ' +
      'baru dibuka menariknya otomatis.',
    status: 'Status',
    active: 'Aktif',
    inactive: 'Nonaktif',
    empty: 'Belum ada outlet.',
    readOnly: 'Sesi ini <b>hanya melihat</b>. Untuk mengubah setelan, masuk lewat pintu kantor pusat.',
    tableService: 'Table service (mulai dari meja)',
    quickService: 'Quick service (langsung menu)',
    tableServiceShort: 'Table service',
    quickServiceShort: 'Quick service',
  },
  nav: { title: 'Navigasi', grpOutlet: 'Jaringan', grpMaster: 'Master data', grpReport: 'Laporan' },
  master: {
    tab: 'Master menu',
    departments: 'Departemen',
    categories: 'Kategori',
    products: 'Produk',
    prices: 'Harga',
    code: 'Kode',
    name: 'Nama',
    channel: 'Kanal',
    price: 'Harga',
    validFrom: 'Berlaku dari',
    validTo: 'Sampai',
    running: 'berjalan',
    inactive: 'nonaktif',
    addPrice: 'Tambah harga',
    pickDepartment: 'Pilih departemen dulu.',
    pickCategory: 'Pilih kategori dulu.',
    pickProduct: 'Pilih produk dulu.',
    noPrice: 'Produk ini belum berharga — kasir tak bisa menjualnya.',
    /** ⚠️ Aturannya disebut di layar; ia bukan detail teknis, ia yang menjaga jawaban atas sengketa struk. */
    priceMovedHint: 'Pengisian harga pindah ke halaman Harga — di sana satu produk bisa diberi harga untuk semua kanal dan semua grup sekaligus.',
    priceRule:
      'Mengubah harga TIDAK menimpa yang lama: baris berjalan ditutup pada tanggal berlaku, lalu baris baru dibuat. ' +
      'Itulah yang membuat "berapa harganya bulan lalu?" selalu bisa dijawab.',
  },
  routing: {
    counter: 'diambil di konter — tanpa tiket',
    counterHint:
      'Pilih `counter` untuk barang yang tak disiapkan siapa pun (rokok, botolan, merchandise): ' +
      'rutekan produknya ke sana, dan pesanannya lewat tanpa menerbitkan tiket. ⛔ Produk yang ' +
      'BELUM dirutekan tetap ditolak — dua hal itu sengaja berbeda.',
    tab: 'Rute dapur',
    outlet: 'Outlet',
    stations: 'Stasiun cetak / KDS',
    station: 'Stasiun',
    kind: 'Jenis',
    code: 'Kode',
    name: 'Nama',
    isDefault: 'Cadangan',
    addStation: 'Stasiun baru',
    stationSubtitle: 'Tujuan fisik tiket di outlet ini. Klik satu baris untuk menyuntingnya.',
    defaultHint:
      'Stasiun cadangan menerima produk yang belum dirutekan. Tanpa satu pun cadangan, pengiriman ' +
      'pesanan DITOLAK — itu disengaja, karena tiket yang hilang tak pernah berbunyi.',
    product: 'Produk',
    routes: 'Tujuan produk ini di outlet terpilih',
    pickOutlet: 'Pilih outlet dulu.',
    pickProduct: 'Pilih produk dulu.',
    noStation: 'Outlet ini belum punya stasiun. Penjualan TETAP jalan, tapi tak satu pun tiket dapur tercetak.',
    /** ⚠️ Kalimat ini yang menjelaskan kenapa layar ini ada. */
    unroutedInfo: 'Sebagian produk belum punya tujuan — tapi stasiun bawaan outlet ini menerbitkan tiket, jadi yang terlewat masih muncul di dapur.',
    unroutedBlocker: '⚠️ Produk tanpa tujuan tak akan muncul di dapur outlet ini. Penjualan tetap jalan — dapur yang tak menerima tiketnya.',
    perOutlet:
      'Rute milik OUTLET, bukan produk: barang yang sama bisa keluar di KDS berbeda di outlet lain — ' +
      'yang menentukan jarak meja penyiapan ke stasiun, bukan jenis makanannya.',
    noRoute: 'Belum ada tujuan di outlet ini. Tanpa rute (atau stasiun cadangan), item ini tak muncul di dapur — penjualannya tetap jalan.',
    remove: 'Lepas',
  },
  product: {
    detail: 'Detail produk',
    taxGroupForward: '⚠️ Hanya berlaku untuk penjualan BERIKUTNYA. Struk yang sudah keluar membawa pajaknya sendiri dan tidak berubah.',
    receiptName: 'Nama di struk',
    receiptHint: 'Kertas struk 32–42 karakter — yang terpotong biasanya justru pembedanya.',
    description: 'Deskripsi',
    sortOrder: 'Urutan tampil',
    control: 'Kontrol',
    discountAllowed: 'Boleh didiskon',
    zeroPriceAllowed: 'Boleh harga 0',
    returnable: 'Boleh diretur',
    controlHint:
      'Ini kontrol yang MEMAKSA, bukan catatan: tanpa "boleh didiskon", rokok & minuman botol bisa ' +
      'didiskon diam-diam — tak pernah muncul sebagai galat, hanya sebagai margin yang menipis.',
    availability: 'Ketersediaan',
    dateRange: 'Masa berlaku',
    timeRange: 'Jam tayang',
    days: 'Hari aktif',
    alwaysHint: 'Kosong = selalu tersedia. Jangan diisi 00:00–23:59 untuk maksud yang sama.',
    barcodes: 'Barcode',
    barcodeAdd: 'Tambah barcode',
    barcodeHint: 'Boleh lebih dari satu — kemasan berbeda punya barcode berbeda.',
    save: 'Simpan',
    saved: 'Tersimpan',
  },
  structure: {
    tab: 'Struktur menu',
    brand: 'Brand',
    perBrand:
      'Departemen & kategori milik BRAND: tiap brand punya struktur menunya sendiri. Buat departemen ' +
      'dulu, baru kategorinya — tak ada penampung "Lainnya" yang lahir sendiri.',
    pickBrand: 'Pilih brand dulu — struktur menu selalu milik satu brand.',
    depSubtitle: 'Pengelompokan teratas: dapur, bar, retail. Ditata sekali, jarang berubah.',
    katSubtitle: 'Isi departemen. Satu kategori selalu punya satu departemen — tak ada yang menggantung.',
    newDepartment: 'Departemen baru',
    newCategory: 'Kategori baru',
    noDepartment: 'Brand ini belum punya departemen.',
    noCategory: 'Belum ada kategori di brand ini.',
    needDepartment: 'Buat departemennya dulu — kategori tak boleh menggantung tanpa induk.',
    sortOrder: 'Urutan',
    codeRule: 'Huruf, angka, - dan _ saja. Tanpa spasi.',
    codeFixed: 'Kode dipakai di struk & laporan — sesudah dibuat ia tak bisa diganti.',
    codeLocked: 'Kode tak bisa diubah: ia sudah tercetak di struk lama dan dipakai di laporan.',
    activeVsOutlet:
      'Nonaktif di sini berarti mati di SELURUH outlet brand ini. Untuk mematikan di satu outlet ' +
      'saja, pakai layar Ketersediaan outlet.',
  },
  priceGroup: {
    /** ⭐⭐ Pengisian harga grup — sebelum ini grup hanya bisa kosong selamanya. */
    /** ⭐⭐ Satu harga → semua kanal. ⚠️ Teksnya menyebut RISIKO, bukan cuma fungsinya. */
    allChannels: 'Semua kanal',
    replicateBtn: '→ semua kanal',
    replicateTitle: 'Pasang harga ini ke SEMUA kanal?',
    replicateWarning:
      'Enam kanal sekaligus, termasuk GoFood/Grab/Shopee. Penyelenggara antar memotong 20–30% ' +
      'komisi — menyamakan harganya dengan dine-in berarti selisihnya dimakan dari margin Anda. ' +
      'Semua kanal akan ditampilkan sesudahnya supaya hasilnya bisa diperiksa.',
    replicateOkBtn: 'Pasang ke semua',
    replicateOk: 'Terpasang di {{n}} kanal',
    replicatePartial: 'Sebagian GAGAL: {{list}} — yang lain sudah tertulis, jangan diulang seluruhnya',
    target: 'Sasaran',
    generalTarget: 'Harga umum (semua outlet)',
    generalHint:
      'Harga dasar yang dipakai setiap outlet yang tak masuk grup mana pun. Isi ini dulu; grup ' +
      'hanya menimpanya untuk outlet yang menunjuknya.',
    channels: 'Kanal',
    fillTitle: 'Isi harga grup',
    pickToFill: 'Pilih grup untuk diisi harganya',
    fillHint:
      'Produk yang dibiarkan kosong mengikuti harga umum — itu keadaan yang sah, bukan yang ' +
      'terlewat. Isi hanya yang memang berbeda di grup ini.',
    basePrice: 'Harga umum',
    groupPrice: 'Harga grup',
    noBasePrice: 'belum ada harga',
    followsBase: 'ikut harga umum',
    noProduct: 'Brand ini belum punya produk. Buat menunya dulu di layar Produk.',
    title: 'Grup harga',
    new: 'Grup harga baru',
    empty: 'Brand ini belum punya grup harga.',
    members: 'Outlet yang ikut',
    noMember: 'belum dipakai outlet mana pun',
    subtitle: 'Satu tarif dipakai bersama beberapa outlet — mal, ruko, bandara.',
    assignHint: 'Outlet menunjuk grupnya di layar Outlet, bukan di sini: kolomnya milik outlet.',
    none: 'Tanpa grup (harga umum)',
    switchHint: 'Mengganti grup memindahkan SELURUH harga outlet ini sekaligus.',
    note:
      'Harga ditulis SEKALI ke grupnya, bukan disalin ke tiap outlet. Urutan yang menang saat ' +
      'kasir mencari harga: harga khusus OUTLET, lalu harga GRUP, lalu harga umum.',
  },
  tax: {
    tabRate: 'Tarif & rezim',
    tabBase: 'Dasar & berlaku',
    doubleTax:
      'PPN & PB1 EKSKLUSIF: produk yang kena PPN tak boleh kena PB1 lagi. Pisahkan ke kelas ' +
      'fiskal berbeda — makanan & jasa restoran ke grup PB1, barang kemasan ke grup PPN.',
    conflictsWith: 'bentrok dengan {{code}}',
    kind: 'Rezim',
    kindHint: 'REZIM, bukan nama pajak. PB1 = local_tax (pajak daerah, tak dikreditkan); PPN = vat.',
    rate: 'Tarif',
    rateHint: 'Isi PERSEN — "11" berarti 11%. Disimpan sebagai pecahan 0,110000.',
    rateFrozen:
      'Tarif TIDAK bisa diubah. Menaikkannya dengan menimpa akan mengubah cara laporan LAMA ' +
      'dihitung ulang, dan selisihnya muncul di SPT. Cara yang benar: akhiri kode ini pada ' +
      'tanggalnya, lalu buat kode baru dengan tarif baru.',
    sequence: 'Urutan',
    sequenceHint: 'Yang kecil dihitung lebih dulu. SC 10 · PB1 20 · PPN 30.',
    base: 'Dasar pengenaan',
    baseHint: 'Dikenakan DI ATAS APA. PB1 khas F&B: net_sales + service_charge.',
    baseRequired: 'Pilih minimal satu dasar pengenaan.',
    inclusive: 'Harga sudah termasuk pajak ini',
    inclusiveHint: 'Bukan "termasuk pajak lain" — harga jualnya sendiri sudah mengandung pajak ini.',
    startsOn: 'Berlaku dari',
    endsOn: 'Berakhir pada',
    endsHint: 'Kosongkan bila masih berjalan. Mengisi tanggal = mengakhiri, bukan menghapus.',
    validity: 'Masa berlaku',
    codes: 'Kode pajak',
    codesSubtitle: 'Pajak apa yang ada & berapa tarifnya. Klik satu baris untuk memilih outletnya.',
    newCode: 'Kode pajak baru',
    outlets: 'Berlaku di outlet',
    pickCode: 'Pilih satu kode pajak untuk memilih outletnya.',
    outletHint:
      'PB1 adalah pajak DAERAH — tarifnya ikut kabupaten/kota outletnya. Jaringan lintas kota ' +
      'wajar punya beberapa tarif PB1 berjalan bersamaan.',
    tab: 'Pajak & service',
    group: 'Kelas fiskal',
    groupHint: 'Menentukan pajak apa yang dikenakan. STANDARD = kena semua; rokok & voucher punya kelasnya sendiri.',
    groups: 'Grup pajak',
    groupsSubtitle: 'Kelas fiskal produk. Klik satu baris untuk memilih kode pajak yang dikenakan.',
    newGroup: 'Grup pajak baru',
    codesApplied: 'Kode pajak yang DIKENAKAN',
    noTaxIntended: 'Sengaja tanpa pajak',
    noCode: 'tak kena pajak apa pun',
    pickGroup: 'Pilih satu grup untuk mengatur kode pajaknya.',
    pickCodesHint: 'Sesudah disimpan, centang kode pajaknya di panel kanan — grup hanya kena yang dicentang.',
    channelLimit: 'Batas kanal',
    channels: 'Kanal',
    allChannels: 'semua kanal',
    channelHint:
      'Kosong = berlaku di SEMUA kanal. Pilih kanal hanya bila pajaknya memang dibatasi — ' +
      'sebagian outlet mengenakan service charge hanya untuk dine_in, sebagian lain (termasuk ' +
      'Arena) mengenakannya juga untuk takeaway. Kosongkan bila tak dibatasi.',
    note:
      'Dua sumbu berbeda: GRUP melekat pada barangnya (rokok tanpa service charge), KANAL melekat ' +
      'pada cara jualnya (takeaway tanpa service charge). Kode pajak baru DIBUAT di sini, tapi ' +
      'tarif yang sudah ada tak bisa diubah — ia diakhiri lalu diganti kode baru.',
  },
  preset: {
    resto_full: 'Resto full-service',
    resto_quick: 'Resto cepat saji',
    coffee_shop: 'Kedai kopi',
    warteg: 'Warung makan',
    minimarket: 'Minimarket / kelontong',
    toko_bangunan: 'Toko bangunan',
    resto_kertas: 'Resto — tiket ditulis tangan',
  },
  capability: {
    kitchen_routing: 'baris wajib dikirim ke dapur sebelum ditagih',
    barcode_lookup: 'item dicari lewat barcode, bukan kisi menu',
    weighing_scale: 'kuantitas datang dari timbangan',
    tiered_price: 'harga bertingkat menurut kuantitas (grosir)',
    recipe_backflush: 'stok berkurang lewat RESEP',
    direct_stock_deduct: 'stok berkurang LANGSUNG per SKU',
  },
  payments: {
    scope: 'Berlaku di',
    allBrands: 'Semua brand',
    scopeHint: 'Kosongkan supaya berlaku di semua brand — itu yang benar untuk tunai & QRIS. Isi brand hanya untuk metode khas brand itu (voucher, kartu hadiah).',
    tab: 'Metode bayar',
    title: 'Metode bayar',
    subtitle: 'Tanpa satu pun metode bayar, outlet TAK BISA berjualan',
    new: 'Metode bayar baru',
    empty: 'Belum ada metode bayar.',
    /* ⛔ Disebut di depan, bukan jadi galat saat kasir membuka sesi. */
    required: 'Outlet wajib punya minimal satu metode bayar — tanpa itu gerbang kesiapan menahannya',
    tenderType: 'Jenis ekonomi',
    /* ⛔⛔ Pelajaran termahal dari VTEC: satu boolean memikul tiga kategori ekonomi. */
    tenderHint: 'Menentukan bagaimana ERP membukukannya. Tak bisa diubah sesudah dibuat',
    typeLocked: 'Jenis ekonomi tak bisa diubah — mengubahnya memindahkan transaksi kemarin ke kategori lain',
    tender: {
      cash: 'Tunai',
      clearing: 'Kliring (kartu/QRIS)',
      liability_release: 'Pelepasan liabilitas (voucher/deposit)',
      receivable: 'Piutang (tagih kemudian)',
      non_tender: 'Bukan pembayaran',
    },
    tenderWhy: {
      cash: 'Uang fisik masuk laci. Wajib dihitung saat tutup sesi, dan selisihnya berbunyi',
      clearing: 'Uang belum di tangan — bank/penyedia menyetorkannya belakangan, sering dipotong MDR',
      liability_release: 'Tamu membayar dengan yang SUDAH dibayar dulu. Ini melunasi utang kita, bukan pendapatan baru',
      receivable: 'Tamu belum membayar sama sekali. Ini piutang, dan ia harus ditagih',
      non_tender: 'Bukan uang: pemberian cuma-cuma, cicip, atau koreksi. Tetap dicatat supaya terlihat',
    },
    opensDrawer: 'Buka laci',
    drawerHint: 'Laci = keputusan metode bayar, bukan sifat printer. QRIS tak membukanya',
    viaEdc: 'Lewat mesin EDC',
    viaEdcHint: 'Kasir wajib menyalin kode approval dari slip EDC. Tanpa itu setoran bank tak bisa dijodohkan dengan struk.',
    requiresReference: 'Wajib nomor referensi',
    referenceHint: 'Kasir tak bisa menutup tanpa nomor approval/EDC',
    provider: 'Penyedia settlement',
    providerHint: 'Bank/penyedia yang menyetorkan — dipakai menjodohkan setoran',
    mdr: 'MDR (%)',
    /* ⛔ MDR ≠ diskon: ia biaya bank atas pendapatan PENUH. */
    mdrHint: 'Biaya penyedia. BUKAN potongan penjualan — omzet tetap penuh',
    erpNote: 'Pemetaan ke akun terjadi di ERP (pos_tender_map), bukan di sini',
  },
  subscription: {
    tab: 'Langganan',
    title: 'Langganan',
    plan: 'Paket',
    validFrom: 'Berlaku sejak',
    validTo: 'Berakhir',
    noEnd: 'Tanpa batas waktu',
    features: 'Hak pakai',
    feature: 'Fitur',
    limit: 'Batas',
    unlimited: 'Tanpa batas',
    noFeature: 'Paket ini belum membuka fitur apa pun.',
    none: 'Belum ada langganan aktif',
    /* ⛔ Jalan keluar DISEBUT — layar tanpa tombol yang tak menyebut ke mana harus menghubungi
       membuat orang menekan-nekan mencari tombol yang memang tak ada. */
    contactOsb: 'Hubungi OSB untuk mengaktifkan atau memperpanjang.',
    expiring: 'Langganan berakhir dalam {{n}} hari',
    expired: 'Langganan sudah berakhir {{n}} hari lalu',
    readOnly: 'Halaman ini hanya menampilkan informasi. Perubahan paket dilakukan OSB.',
    loadFailed: 'Informasi langganan gagal dimuat',
  },
  staff: {
    tab: 'Staf & peran',
    title: 'Staf',
    subtitle: 'Siapa yang boleh masuk ke kasir, dan sejauh apa',
    new: 'Staf baru',
    empty: 'Belum ada staf.',
    code: 'Kode staf',
    position: 'Jabatan',
    /* ⭐⭐ Rantainya disebut di layar: izin menempel pada JABATAN, bukan pada orang. */
    chain: 'Izin sampai ke orang lewat rantai: staf → jabatan → peran → izin. Ubah izin di peran, bukan per orang.',
    positionHint: 'Dikosongkan = staf ini belum punya izin apa pun',
    noPosition: 'Tanpa jabatan',
    positionUnmapped: 'Jabatan belum dipetakan ke peran',
    hrSource: 'Sumber',
    source: { local: 'Dibuat di portal', external: 'Dari HR' },
    /* ⛔ Batas K8 disebut di layar supaya tak ada yang mencarinya. */
    k8: 'POS tidak menyimpan gaji, KTP, maupun foto — itu milik aplikasi HR.',
    pin: 'PIN',
    setPin: 'Ganti PIN',
    setPinFor: 'Ganti PIN — {{name}}',
    /* ⛔ GANTI, bukan LIHAT: yang tersimpan hash-nya. */
    pinHint: '4–8 digit. PIN lama tak bisa dilihat — yang tersimpan hanya sidik amannya.',
    roles: 'Peran & izin',
    permission: 'Izin',
    noRole: 'Belum ada peran. Tanpa peran, jabatan tak bisa dipetakan dan staf tak punya izin.',
  },
  modifier: {
    tab: 'Modifier',
    groups: 'Grup modifier',
    groupsSubtitle: 'Ditata sekali, dipakai banyak produk',
    newGroup: 'Grup baru',
    noGroup: 'Belum ada grup modifier.',
    /* ⚠️ Disebut supaya yang mencari "pasang ke produk" tak menyimpulkan fiturnya hilang. */
    attachHint: 'Grup ditata di sini; memilih grup mana yang berlaku untuk sebuah produk dilakukan di layar Produk.',
    rule: 'Aturan pilih',
    required: 'Wajib pilih {{n}}',
    optional: 'Opsional',
    max: 'Maks {{n}}',
    /* ⚠️ 0 = tanpa batas, bukan "tak boleh memilih". */
    maxNone: 'Maks bebas',
    minSelect: 'Minimal pilih',
    minHint: '≥1 berarti WAJIB dijawab — baris tak bisa disimpan tanpa itu',
    maxSelect: 'Maksimal pilih',
    maxHint: '0 = tanpa batas',
    freeText: 'Teks bebas',
    freeTextHint: 'Mis. "sausnya dipisah"',
    /* ⛔ §5d — teks bebas mengubah cara membuat, bukan yang dibayar. */
    freeTextNeverPriced: 'Teks bebas tidak pernah menambah harga — ia mengubah cara membuat, bukan yang dibayar.',
    pickGroup: 'Pilih satu grup untuk melihat opsinya.',
    optionsOf: 'Opsi — {{name}}',
    options: 'Opsi',
    newOption: 'Opsi baru',
    noOption: 'Grup ini belum punya opsi.',
    requiredWarning: 'Grup ini WAJIB dijawab. Tanpa opsi, kasir tidak bisa menyimpan baris yang memakainya.',
    kind: 'Jenis',
    kindHint: 'Menentukan apakah ia menambah harga dan memotong stok',
    kinds: {
      free: 'Gratis',
      priced: 'Menambah harga',
      priced_stock: 'Menambah harga + potong stok',
    },
    priceDelta: 'Tambahan harga',
    priceHint: 'Diabaikan untuk jenis Gratis',
    forProduct: 'Modifier untuk produk ini',
    applies: 'Berlaku',
    noGroupYet: 'Belum ada grup modifier. Buat dulu di layar Modifier.',
    /* ⛔ Grup wajib mengubah alur kasir — disebut sebelum dipasang, bukan sesudah. */
    attachWarning: 'Grup yang WAJIB akan menahan kasir sampai dijawab. Pastikan grupnya punya opsi.',
  },
  crud: {
    search: 'Cari…',
    empty: 'Belum ada data.',
    edit: 'Sunting',
    save: 'Simpan',
    cancel: 'Batal',
    saved: 'Tersimpan',
  },
  products: {
    kindSimple: 'Biasa',
    kindCombo: 'Paket / combo',
    kindHint: '⛔ Tak bisa diubah sesudah produk dibuat: baris pesanan lama akan berselisih dengan masternya. Pilih PAKET bila isinya produk lain — penyusunnya muncul di detail produk.',
    tab: 'Produk',
    kind: 'Jenis',
    new: 'Produk baru',
    empty: 'Belum ada produk di kategori ini.',
    subtitle: 'Klik satu baris untuk membuka detailnya: kontrol, ketersediaan, barcode, dan harga.',
  },
  availability: {
    tab: 'Ketersediaan outlet',
    outlet: 'Outlet',
    available: 'Tersedia',
    inherited: 'ikut induknya',
    note:
      'Bawaannya seluruh menu brand tersedia di semua outletnya; yang disimpan di sini hanya ' +
      'PENGECUALIAN. Mematikan departemen/kategori otomatis menutup seluruh isinya di outlet itu.',
  },
  combo: {
    title: 'Susunan paket (combo)',
    qtyPerOption: 'Dapat berapa',
    qtyPerOptionHint:
      'Jumlah yang keluar kalau opsi INI dipilih. "Roti canai ayam 2, beef 1" ditulis di sini — ' +
      'bukan dengan melebarkan min/max, yang justru menyerahkan jumlahnya ke ingatan pelayan.',
    sharedBy: 'dipakai {{n}} set menu',
    reusePick: 'Pakai grup yang sudah ada',
    reuseAdd: '+ Pakai grup ini',
    detach: 'Lepas',
    detachGroup: 'Lepas grup ini dari set menu ini? Grupnya tetap ada untuk set menu lain.',
    editLinkHint: 'Hanya untuk set menu ini. Set menu lain yang memakai grup yang sama tak berubah.',
    fixed: 'selalu ikut',
    fixedPick: 'Komponen yang selalu ikut',
    addFixed: '+ Komponen tetap',
    editGroup: 'Sunting',
    editHint:
      'min dipaksa server saat kirim ke dapur. Menaikkannya pada paket yang sudah dijual: bill ' +
      'lama tetap sah, yang baru menuntut lebih.',
    preview: 'Beginilah nanti di layar kasir',
    previewFixed: '— {{item}} (otomatis, kasir tak memilih)',
    previewChoice: '— kasir pilih {{n}} dari {{total}}',
    impossible: 'MUSTAHIL: opsinya kurang dari min',
    hint: 'Grup = satu pilihan tamu (mis. "Pilih minuman"). Komponen TETAP = grup dengan satu opsi, min=maks=1. ⚠️ Tambahan harga = SELISIH, bukan harga komponen.',
    empty: 'Produk ini berjenis combo tapi belum punya grup — ia akan terjual sebagai paket KOSONG: tamu membayar, dapur tak menerima satu komponen pun.',
    addGroup: 'Tambah grup',
    groupCode: 'Kode grup',
    groupName: 'Nama grup',
    min: 'Min',
    max: 'Maks',
    exactly: 'pilih tepat {{n}}',
    range: 'pilih {{min}}–{{max}}',
    component: 'Komponen',
    qty: 'Jumlah',
    upcharge: 'Tambahan harga',
    default: 'Bawaan',
    addOption: 'Tambah opsi',
    noOption: 'Belum ada opsi — grup tanpa opsi tak bisa dipilih kasir',
    deleteGroup: 'Hapus grup ini beserta opsinya?',
  },
  promoTarget: {
    level: 'Tingkat',
    what: 'Berlaku untuk',
    department: 'Departemen',
    category: 'Kategori',
    product: 'Produk',
    pick: 'Pilih departemen / kategori / produk',
    add: 'Tambah sasaran',
    none: 'Belum ada sasaran',
    emptyMeans: '⚠ Tanpa sasaran, promo ini berlaku ke SEMUA baris yang boleh didiskon — persis seperti promo se-bill. Tambahkan sasaran untuk mempersempitnya.',
    narrows: 'Promo hanya berlaku pada baris di bawah ini. Satu baris tingkat departemen sudah mencakup seluruh kategori & produk di bawahnya.',
  },
  history: {
    title: 'Riwayat perubahan',
    when: 'Waktu',
    action: 'Aksi',
    actor: 'Oleh',
    changed: 'Yang berubah',
    empty: 'Belum ada perubahan tercatat untuk baris ini',
  },
  /**
   * Pengguna konsol (Imron, 2026-08-07) — ⚠️ POPULASI BERBEDA dari layar Staf.
   * Yang di sini masuk dengan email+sandi untuk mengurus master & laporan; yang di Staf masuk
   * dengan kode+PIN untuk bekerja di lantai. Satu orang boleh ada di dua-duanya.
   */
  consoleUsers: {
    tab: 'Pengguna konsol',
    email: 'Email',
    name: 'Nama',
    role: 'Peran',
    staff: 'Staf',
    lastLogin: 'Terakhir masuk',
    never: 'Belum pernah',
    status: 'Status',
    active: 'Aktif',
    inactive: 'Nonaktif',
    locked: 'Terkunci',
    add: 'Tambah pengguna',
    create: 'Terbitkan',
    created: '{{email}} sudah bisa masuk',
    password: 'Kata sandi awal',
    weak: 'Minimal {{n}} karakter',
    reset: 'Setel ulang sandi',
    resetFor: 'Setel ulang sandi {{email}}',
    resetHint:
      'Sandi baru berlaku langsung, dan akunnya ikut DIBUKA kuncinya kalau sedang terkunci. ' +
      'Sampaikan ke orangnya lewat jalur yang aman, lalu minta ia menggantinya sendiri.',
    resetOk: 'Sandi disetel ulang',
    resetUnlocked: 'Sandi disetel ulang — akunnya ikut dibuka kuncinya',
    deactivate: 'Nonaktifkan',
    activate: 'Aktifkan',
    roleName: { owner: 'Owner', admin: 'Administrator', manager: 'Manager', viewer: 'Viewer' },
    ownerProtected: 'Akun pemilik — hanya pemilik yang bisa mengubahnya',
    roleHint:
      'Viewer hanya membaca · Manager boleh mengubah master · Administrator ditambah mengelola '
      + 'pengguna (⛔ kecuali akun Owner) · Owner boleh semuanya. ' +
      '⛔ Pemilik aktif terakhir tak bisa dinonaktifkan — kalau tidak, tenant ini kehilangan ' +
      'satu-satunya pintu kelolanya dan tak ada jalan pulang.',
    vsStaff:
      'Ini daftar orang yang masuk PORTAL dengan email & kata sandi. Kasir, pelayan, dan juru ' +
      'masak ada di layar Staf — mereka masuk dengan kode staf & PIN, dan memang bukan orang yang ' +
      'sama. Satu orang boleh ada di dua-duanya.',
  },
  password: {
    button: 'Ubah sandi',
    title: 'Ubah kata sandi saya',
    current: 'Kata sandi sekarang',
    next: 'Kata sandi baru',
    repeat: 'Ketik ulang kata sandi baru',
    mismatch: 'Ketikan ulangnya belum sama',
    weak: 'Minimal {{n}} karakter',
    save: 'Simpan',
    ok: 'Kata sandi diganti',
    hint:
      'Sandi lama diminta supaya sesi yang bocor tak bisa dipakai mengambil alih akun ini secara ' +
      'permanen. Sesudah diganti, sesi yang sedang berjalan tetap berlaku sampai kedaluwarsa.',
  },
  /** Template struk — satu-satunya bagian sistem yang dibaca TAMU. */
  /** Notifikasi keluar (0068) — kotak masuk & tujuan per outlet. */
  notify: {
    tab: 'Notifikasi',
    tabInbox: 'Kotak masuk',
    tabTargets: 'Tujuan',
    colWhen: 'Waktu',
    colOutlet: 'Outlet',
    colKind: 'Jenis',
    colMessage: 'Pesan',
    colDelivery: 'Pengiriman',
    colChannel: 'Kanal',
    colAddress: 'Alamat',
    colLabel: 'Keterangan',
    colKinds: 'Dilanggan',
    colHealth: 'Status',
    allOutlets: 'Semua outlet',
    addTarget: 'Tambah tujuan',
    targetTitle: 'Tujuan notifikasi',
    addressHelp:
      'Telegram: chat_id. Kirim pesan apa pun ke bot notifikasi, lalu minta chat_id-nya ke admin.',
    needKind: 'Pilih minimal satu jenis',
    test: 'Uji kirim',
    testOk: 'Terkirim — cek Telegram',
    testNone: 'Tak ada yang terkirim; lihat kolom Status',
    healthy: 'Sehat',
    noTarget: 'Tak ada penerima',
    sent: '{{n}} terkirim',
    pending: '{{n}} antre',
    failed: '{{n}} gagal',
    noChannelTitle: 'Belum ada kanal yang bisa mengirim',
    noChannelDesc:
      'Peristiwa tetap dicatat di kotak masuk, tapi tak ada yang dikirim ke luar. '
      + 'Minta operator menyetel token bot Telegram di server.',
    kind: {
      shift_variance: 'Selisih kas',
      day_closed: 'Tutup hari',
      sale_rejected: 'Penjualan ditolak',
    },
  },
  /** Stok, bahan & resep (0069). */
  stock: {
    tab: 'Stok & resep',
    tabOnHand: 'Saldo',
    tabMovements: 'Pergerakan',
    tabRecipes: 'Resep',
    tabMaterials: 'Bahan',
    colKind: 'Jenis',
    colCode: 'Kode',
    colName: 'Nama',
    colUom: 'Satuan',
    colOnHand: 'Saldo',
    colTheoretical: 'Dari hitungan resep',
    colDate: 'Tanggal',
    colType: 'Pergerakan',
    colQty: 'Jumlah',
    colReason: 'Alasan',
    colOrder: 'Bill',
    colActor: 'Oleh',
    colProduct: 'Produk',
    colYield: 'Hasil',
    colLines: 'Bahan',
    colNote: 'Catatan',
    pickOutlet: 'Pilih outlet',
    pickOutletFirst: 'Pilih outlet dulu — saldo stok selalu milik satu outlet.',
    receive: 'Terima barang',
    waste: 'Catat susut',
    addMaterial: 'Tambah bahan',
    addRecipe: 'Buat resep untuk produk…',
    addLine: 'bahan',
    recipeTitle: 'Resep {{code}}',
    recipeDeleted: 'Resep dihapus karena tak ada bahannya',
    reasonRequired: 'Alasan wajib diisi',
    theoretical: 'teoretis',
    coverageTitle: 'Resep menutup',
    coverageHelp:
      'Diukur dari NILAI penjualan, bukan jumlah menu. Menu tanpa resep tidak memotong stok '
      + 'sama sekali — dan itu keadaan yang wajar, asal terlihat.',
    topUncovered: 'Belum tertutup:',
    uomWarning:
      'Satu satuan dipakai untuk resep, penerimaan, dan opname sekaligus. Tak ada konversi '
      + 'otomatis — kalau resep memakai gram, terima juga dalam gram.',
    yieldHelp: 'Berapa porsi jadi dari satu resep. Isi 20 kalau satu adonan jadi 20 roti.',
    emptyRecipeWarning:
      'Resep tanpa bahan akan DIHAPUS, bukan disimpan kosong. Resep kosong membuat produknya '
      + 'terhitung "sudah ada resep" padahal tak memotong apa pun.',
    kind: { product: 'Produk', material: 'Bahan' },
    type: {
      receipt: 'Terima',
      sale_backflush: 'Terjual (resep)',
      sale_direct: 'Terjual (1:1)',
      waste: 'Susut',
      transfer_in: 'Transfer masuk',
      transfer_out: 'Transfer keluar',
      opname_adjustment: 'Koreksi opname',
    },
    reason: {
      spoilage: 'Basi / rusak',
      breakage: 'Pecah / tumpah',
      expired: 'Kedaluwarsa',
      staff_meal: 'Makan karyawan',
      other: 'Lainnya',
    },
  },
  report: {
    tab: 'Laporan',
    salesTitle: 'Penjualan per hari',
    kitchenTitle: 'Waktu masak dapur',
    station: 'Stasiun',
    tickets: 'Tiket',
    avg: 'Rata-rata',
    late: 'Terlambat',
    unfinished: 'Belum selesai',
    itemTitle: 'Penjualan per item',
    /* ⭐ Judul menyebut AKIBATNYA, bukan mekanismenya: "penyimpangan harga" bisa ditindaklanjuti,
       "sale_price_drift" tidak. */
    drift: {
      title: 'Penyimpangan harga (till vs master)',
      none: 'Tak ada penyimpangan — semua bill dijual dengan harga master.',
      order: 'Bill',
      device: 'Perangkat',
      product: 'Produk',
      qty: 'Qty',
      charged: 'Ditagihkan',
      master: 'Master',
      delta: 'Selisih',
      catalog: 'Versi katalog',
    },
    item: 'Item',
    category: 'Kategori',
    qtySold: 'Terjual',
    netSold: 'Nilai jual',
    refunded: 'Dikembalikan',
    netAfterRefund: 'Bersih',
    share: 'Porsi',
    taxTitle: 'Rekap pajak (untuk pelaporan)',
    taxCode: 'Kode',
    taxName: 'Nama',
    taxKind: 'Jenis',
    inclusive: 'INKLUSIF',
    rate: 'Tarif',
    dpp: 'DPP',
    taxAmount: 'Pajak',
    downloadCsv: 'Unduh CSV',
    /* ⚠️ Menyebut bruto & total supaya selisih "DPP vs seluruh penjualan" bisa dijelaskan sendiri. */
    taxBaseHint:
      'Dari {{bills}} bill LUNAS pada periode ini: bruto Rp {{gross}}, total dibayar Rp {{grand}}. ' +
      'DPP di bawah bisa lebih kecil dari bruto — produk berkelas fiskal bebas & pemberian ' +
      'cuma-cuma memang tak punya dasar pengenaan.',
    /* ⛔⛔ Dua jalur menuju angka yang sama; kalau berbeda, JANGAN disalin ke pelaporan. */
    taxMismatch:
      '⛔ Rekap pajak (Rp {{recap}}) TIDAK sama dengan total pajak di pesanan (Rp {{rollup}}). ' +
      'Jangan salin angka ini ke pelaporan sebelum sebabnya ditemukan — keduanya dihitung dari ' +
      'tabel yang berbeda, dan selisihnya berarti salah satunya keliru.',
    tenderTitle: 'Setoran per jenis tender',
    shiftTitle: 'Tutup sesi kas',
    date: 'Tanggal usaha',
    outlet: 'Outlet',
    allOutlets: 'Semua outlet',
    refresh: 'Muat ulang',
    bills: 'Bill',
    guests: 'Tamu',
    gross: 'Bruto',
    discount: 'Diskon',
    serviceCharge: 'Service Charge',
    tax: 'Pajak',
    grandTotal: 'Total',
    tenderTotal: 'Σ tender',
    tenderType: 'Jenis',
    count: 'Trx',
    amount: 'Masuk',
    received: 'Uang diterima',
    change: 'Kembalian',
    shiftNo: 'Nomor sesi',
    openedBy: 'Dibuka oleh',
    status: 'Status',
    stillOpen: 'MASIH TERBUKA',
    expectedCash: 'Kas seharusnya',
    countedCash: 'Kas terhitung',
    variance: 'Selisih',
    reason: 'Alasan',
    summary: '{{bills}} bill · {{guests}} tamu · Rp {{amount}}',
    /* ⛔⛔ Bukan peringatan halus: Σ tender ≠ Σ penjualan berarti DATAnya rusak, bukan tampilannya. */
    imbalance:
      '⛔ {{n}} hari yang setoran tendernya TIDAK sama dengan penjualannya. Ini seharusnya mustahil ' +
      '(Σ tender = total bill selalu ditegakkan saat menutup). Jangan pakai angkanya sebelum ' +
      'sebabnya ditemukan — mulai dari bill yang dibuka kembali di hari itu.',
    /* ⚠️ Beda "tanggal usaha" vs "tanggal kalender" disebut, bukan dibiarkan jadi kejutan. */
    businessDateHint:
      'Dikelompokkan menurut TANGGAL USAHA — hari berganti saat cutover (04:00/05:00), bukan tengah ' +
      'malam. Bill jam 01:00 masih milik hari sebelumnya.',
  },
  receipt: {
    tab: 'Struk',
    title: 'Kepala & kaki struk',
    pickOutlet: 'Pilih outlet',
    uploadLogo: 'Unggah logo',
    removeLogo: 'Hapus logo',
    logoHint:
      'PNG/JPG apa saja — diubah jadi hitam-putih selebar kertas saat diunggah. Logo yang sudah ' +
      'tegas hitam-putih hasilnya paling bagus; foto akan terlihat berbintik di printer termal. ' +
      '⚠️ Ganti lebar kertas ⇒ unggah ulang logonya.',
    logoTooWide:
      'Logo ini lebih lebar dari kertas {{mm}} mm ⇒ printer akan MELEWATINYA — struk keluar tanpa ' +
      'logo, tanpa pesan galat. Unggah ulang selagi lebar {{mm}} mm terpilih.',
    address1: 'Alamat baris 1',
    address2: 'Alamat baris 2',
    footnote: 'Catatan kaki (mis. "Barang yang sudah dibeli tidak dapat ditukar")',
    footnoteTitle: 'Catatan kaki',
    footnoteN: 'Baris {{n}} — kosong berarti tak dicetak',
    fillExample: 'Isi contoh',
    fontTitle: 'Ukuran huruf isi',
    fontNormal: 'Normal',
    fontSmall: 'Kecil (muat lebih banyak)',
    nameScaleTitle: 'Nama outlet',
    scaleNormal: 'Normal',
    scaleLarge: 'Besar',
    save: 'Simpan',
    saved: 'Template struk disimpan',
    preview: 'Pratinjau · {{mm}} mm',
    previewHint:
      'Isi & angkanya contoh; yang nyata datang dari pesanan. Logo tampil sebagai penanda karena ' +
      'pratinjau ini teks — ukurannya yang tertulis itulah yang akan tercetak.',
    hint:
      'Yang disetel di sini tercetak di struk tamu: logo dan alamat di kepala, catatan kaki di ' +
      'bawah. Pratinjaunya dibuat oleh kode yang SAMA dengan yang mengirim ke printer — jadi yang ' +
      'terlihat di sini itulah yang keluar dari kertas.',
  },
  integration: {
    tab: 'Integrasi',
    title: 'Klien integrasi',
    subtitle:
      'Kunci untuk skrip laporan, BI, dan server MCP. Tiap integrasi punya kuncinya SENDIRI — ' +
      'kunci bersama adalah kunci yang tak pernah dicabut.',
    scopeWhy:
      'Yang bisa dipilih di sini hanya izin BACA. Izin platform, kasir, dan tulis-master sengaja ' +
      'tak ada — bukan karena layarnya belum jadi; servernya memang menolaknya.',
    add: 'Klien baru',
    create: 'Buat & tampilkan kunci',
    client: 'Client ID',
    scopes: 'Izin',
    name: 'Nama',
    nameHint: 'Awalan "t:<tenant>:" dipasang server — nama yang sama di tenant lain tak bentrok.',
    nameRule: 'Huruf kecil, angka, tanda hubung; 3–40 karakter.',
    agent: 'Agen AI',
    agentQ: 'Ini agen AI?',
    agentHint:
      'Perbuatannya tercatat sebagai "agent", bukan "system" — supaya pertanyaan "mana yang ' +
      'dikerjakan AI?" punya jawaban. Ini DATA, bukan tebakan dari nama.',
    keyTitle: 'Kunci privat — ditampilkan sekali',
    keyOnce:
      'Salin sekarang. Server hanya menyimpan kunci PUBLIK, jadi ini tak bisa ditampilkan lagi ' +
      'dan tak bisa dipulihkan. Kalau hilang: buat klien baru, lalu cabut yang ini.',
    keySaved: 'Sudah saya simpan',
    'scope.master:read': 'membaca menu, harga, dan pajak',
    'scope.reports:read': 'membaca laporan & ringkasan',
    'scope.ops:read': 'membaca kesehatan sinkron & antrean',
  },
  promo: {
    tabTarget: 'Sasaran',
    tabValidity: 'Berlaku',
    validityHint: 'Kosongkan = berlaku selamanya. Mesin promo MENOLAK sendiri di luar masa berlaku — jadi promo lebaran tak perlu diingat untuk dimatikan.',
    validFrom: 'Mulai tanggal',
    validTo: 'Sampai tanggal',
    timeFrom: 'Mulai jam',
    timeTo: 'Sampai jam',
    days: 'Hari berlaku',
    daysHint: 'Kosong = semua hari. Dipakai untuk promo akhir pekan atau hari kerja.',
    daysAll: 'Semua hari',
    day: { 1: 'Senin', 2: 'Selasa', 3: 'Rabu', 4: 'Kamis', 5: 'Jumat', 6: 'Sabtu', 7: 'Minggu' },
    tabValue: 'Nilai & status',
    tab: 'Promo',
    code: 'Kode',
    name: 'Nama',
    kind: 'Jenis',
    value: 'Nilai',
    cap: 'Maks potongan',
    add: 'Promo baru',
    scope: 'Cakupan',
    subtitle: 'Potongan milik brand. Nilainya dipakai apa adanya oleh kasir — periksa sebelum menyimpan.',
    frozen: 'Kode, jenis, dan cakupan tak bisa diubah: ketiganya sudah ikut tercatat di pesanan yang lewat.',
    capHint: 'Batas rupiah untuk promo persen. Kosong = tanpa batas.',
    minSubtotal: 'Belanja minimum',
    minSubtotalHint:
      'Dihitung dari baris yang MEMENUHI SYARAT saja — rokok & barang yang tak boleh didiskon ' +
      'tidak ikut. Kosong = tanpa minimum.',
    needsApproval: 'Butuh persetujuan penyelia',
    needsApprovalHint:
      'Kasir akan diminta kode & PIN penyelia ber-izin "promo:approve" sebelum promo ini masuk, ' +
      'dan penyetujunya tercatat di bill.',
    activeVsOutlet:
      'Nonaktif di sini berarti mati di SELURUH outlet brand ini. Untuk satu outlet saja, pakai ' +
      'sakelar di kolom "Aktif per outlet".',
    perOutlet: 'Aktif per outlet',
    pickOutlet: 'Pilih outlet untuk menyalakan/mematikan promo di sana.',
    note:
      'Promo brand berlaku di SELURUH outletnya; yang disimpan hanya PENGECUALIAN per outlet. ' +
      'Voucher GoFood/Grab BUKAN promo — uangnya ditagihkan ke penyelenggara, jadi ia metode bayar ' +
      '(piutang), bukan potongan pendapatan kita.',
  },
  /* ⚠️ `on`/`off` sengaja pendek: kata ini muat DI DALAM tuas sakelar, bukan di sebelahnya. */
  common: { logout: 'Keluar', language: 'Bahasa', on: 'Nyala', off: 'Mati',
            save: 'Simpan', saved: 'Tersimpan' },
};
